import { Eyebrow, RadioCard, RadioCardGroup, Stack } from '@leanin/ui';

// The access-policy radio group used by Circle / Network / Space Visibility
// tabs. Centralizes the wording so a copy tweak doesn't drift between the
// surfaces, and lets the entity label vary (Circle vs Network). Renders bare
// (no Card chrome) so the Visibility tab can compose access + join into a
// single card.

export type AdminAccess = 'public' | 'unlisted' | 'private';

type Props = {
  readonly value: AdminAccess;
  readonly onChange: (next: AdminAccess) => void;
  /** "Circle" / "Network" — wordings reference the entity by name. */
  readonly entityLabel: string;
  /** Access options to hide from the picker for this entity. Circles
   *  intentionally omit `unlisted` — leaders shouldn't surface a
   *  reachable-by-link-but-not-listed mode that the rest of the product
   *  copy doesn't explain. */
  readonly omit?: ReadonlyArray<AdminAccess>;
};

export function AdminAccessPolicyChoice({ value, onChange, entityLabel, omit }: Props) {
  const allOptions: ReadonlyArray<{ value: AdminAccess; label: string; lede: string }> = [
    {
      value: 'public',
      label: 'Public',
      lede: `Anyone on Lean In Connect can find this ${entityLabel} in directory listings and search.`,
    },
    {
      value: 'unlisted',
      label: 'Unlisted',
      lede: 'Reachable by direct link, but hidden from directory listings and search.',
    },
    {
      value: 'private',
      label: 'Private',
      lede: `Non-members can’t see this ${entityLabel} at all — even with a direct link.`,
    },
  ];
  const hidden = new Set(omit ?? []);
  const options = allOptions.filter((o) => !hidden.has(o.value));

  return (
    <Stack gap="md">
      <Eyebrow tone="accent">Visibility</Eyebrow>
      <RadioCardGroup
        value={value}
        onValueChange={(v) => onChange(v as AdminAccess)}
        aria-label={`${entityLabel} visibility`}
      >
        {options.map((opt) => (
          <RadioCard
            key={opt.value}
            value={opt.value}
            title={opt.label}
            sub={opt.lede}
          />
        ))}
      </RadioCardGroup>
    </Stack>
  );
}
